import { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Autocomplete,
  Box,
  Checkbox,
  FormInputText,
  InputLabel,
  Svg,
  SvgBubble,
  Text,
  TextField,
  Tooltip,
} from '@hopskipdrive/react-components';
import PhoneIcon from '@mui/icons-material/Phone';
import { FormHelperText } from '@mui/material';
import { Button } from '@rideiq/shared/components/Button';

import { ContactGroupFlexRow, ContactGroupFormContainer } from './MustBeMetProtocolForm.styles';
import { AuthorizedContactGroup, Contact } from './types';
import { contactGroupSchema } from './validationSchema';

interface ContactGroupFormProps {
  index: number;
  contacts: Contact[];
  handleSaveGroup: (index: number, data: AuthorizedContactGroup) => void;
  handleDelete: () => void;
  handleCancel: () => void;
  isEdit: boolean;
  isOnMasterSchoolList?: boolean;
  defaultValues?: AuthorizedContactGroup;
  generateDefault?: boolean;
}

export const ContactGroupForm: FC<ContactGroupFormProps> = ({
  index,
  contacts,
  handleSaveGroup,
  handleDelete,
  handleCancel,
  isEdit,
  isOnMasterSchoolList,
  defaultValues,
}) => {
  const contactsWithPhoneNumber = contacts.filter((contact) => !!contact.mobilePhoneNumber);

  const getDefaultValues = () => {
    if (defaultValues) {
      return defaultValues;
    }

    if (!isOnMasterSchoolList) {
      return {
        name: 'Trusted contacts',
        escalationPhoneNumber: contactsWithPhoneNumber.length ? contactsWithPhoneNumber[0].mobilePhoneNumber : '',
        verifyIdBadge: false,
        verifyRiderName: true,
        verifyRiderBirthdate: true,
      };
    }

    return {
      name: '',
      escalationPhoneNumber: '',
      verifyIdBadge: false,
      verifyRiderName: false,
      verifyRiderBirthdate: false,
    };
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AuthorizedContactGroup>({
    resolver: yupResolver(contactGroupSchema),
    defaultValues: getDefaultValues(),
  });

  const [phoneInputValue, setPhoneInputValue] = useState('');

  const formatPhoneNumber = (value) => {
    let cleaned = value.replace(/\D/g, '');

    // Remove +1 if it's present at the start
    if (cleaned.startsWith('1') && cleaned.length === 11) {
      cleaned = cleaned.slice(1);
    }

    // Format as (XXX) XXX-XXXX
    if (cleaned.length < 1) {
      return '';
    } else if (cleaned.length <= 3) {
      return `(${cleaned}`;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }

    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const onSubmit = (data) => {
    handleSaveGroup(index, data);
  };

  return (
    <ContactGroupFormContainer>
      <Box
        display="flex"
        justifyContent="space-between"
        sx={{
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Text>{(isEdit ? 'Edit' : 'New') + ' authorized individual group'}</Text>
        <Box display="flex" gap="1rem">
          <Button color="tertiary" onClick={handleCancel}>
            Cancel
          </Button>
          {isEdit && (
            <Button color="destructive" onClick={handleDelete}>
              Delete
            </Button>
          )}
          <Button onClick={handleSubmit(onSubmit)} variant="contained" color="primary">
            {isEdit ? 'OK' : 'Add group'}
          </Button>
        </Box>
      </Box>
      <ContactGroupFlexRow>
        <InputLabel fieldtype="required" htmlFor="name">
          Group name
        </InputLabel>
        <Controller
          name="name"
          control={control}
          render={({ field: { value, onChange } }) => (
            <FormInputText
              id="login-email"
              placeholder="Name"
              variant="outlined"
              error={!!errors.name}
              helperText={errors.name?.message}
              autoFocus
              value={value}
              onChange={onChange}
            />
          )}
        />
        <InputLabel htmlFor="escalation-phone-number">Escalation number</InputLabel>
        <Box
          display="flex"
          alignItems="center"
          gap="1rem"
          sx={{ width: { xs: '100%', sm: 'auto' }, flex: { xs: 'unset', sm: 1 } }}
        >
          <Controller
            name="escalationPhoneNumber"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Autocomplete
                freeSolo
                value={value}
                inputValue={phoneInputValue}
                onInputChange={(_, newValue) => {
                  const rawPhoneNumber = newValue.replace(/\D/g, '').slice(0, 10);
                  const formattedValue = formatPhoneNumber(rawPhoneNumber);

                  setPhoneInputValue(formattedValue);
                  onChange(rawPhoneNumber);
                }}
                onChange={(_, newValue) => {
                  if (typeof newValue === 'string') {
                    const rawPhoneNumber = newValue.replace(/\D/g, '').slice(0, 10);
                    const formattedValue = formatPhoneNumber(rawPhoneNumber);

                    setPhoneInputValue(formattedValue);
                    onChange(rawPhoneNumber);
                  } else if (newValue?.mobilePhoneNumber) {
                    const formattedValue = formatPhoneNumber(newValue.mobilePhoneNumber);

                    setPhoneInputValue(formattedValue);
                    onChange(newValue.mobilePhoneNumber);
                  }
                }}
                getOptionLabel={(option) =>
                  typeof option === 'string'
                    ? formatPhoneNumber(option.replace(/\D/g, ''))
                    : `${formatPhoneNumber(option.mobilePhoneNumber)} (${
                        option.firstName && option.lastName
                          ? `${option.firstName} ${option.lastName}`
                          : option.firstName || option.lastName
                      })`
                }
                options={contactsWithPhoneNumber}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    fullWidth
                    data-testid={`escalation-phone-number${index}`}
                    placeholder="Select"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <PhoneIcon />,
                    }}
                    error={!!errors.escalationPhoneNumber}
                    helperText={errors.escalationPhoneNumber?.message}
                  />
                )}
              />
            )}
          />
          <Tooltip title="CareDrivers will contact this number if they have trouble at drop-off.  Enter the number of someone who will be available and able to assist.">
            <SvgBubble>
              <Svg icon="question" />
            </SvgBubble>
          </Tooltip>
        </Box>
      </ContactGroupFlexRow>
      <Box display="flex" flexDirection="column" gap="1rem">
        <InputLabel fieldtype="required">Verification requirements</InputLabel>
        {errors.verifyIdBadge && <FormHelperText error={true}>{errors.verifyIdBadge.message}</FormHelperText>}

        <Box
          display="flex"
          sx={{
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: '4px', sm: '.5rem' },
            alignItems: { xs: 'flex-start', sm: 'center' },
          }}
        >
          <Box display="flex" alignItems="center" gap=".5rem">
            <Controller
              name="verifyIdBadge"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Checkbox id="verify-id-badge" checked={value} onChange={(e) => onChange(e.target.checked)} />
              )}
            />
            <InputLabel htmlFor="verify-id-badge">Show organization ID</InputLabel>
          </Box>
          <Box display="flex" alignItems="center" gap=".5rem">
            <Controller
              name="verifyRiderName"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Checkbox id="verify-rider-name" checked={value} onChange={(e) => onChange(e.target.checked)} />
              )}
            />
            <InputLabel htmlFor="verify-rider-name">{'Know  rider\u2019s name'}</InputLabel>
          </Box>
          <Box display="flex" alignItems="center" gap=".5rem">
            <Controller
              name="verifyRiderBirthdate"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Checkbox id="verify-rider-birthdate" checked={value} onChange={(e) => onChange(e.target.checked)} />
              )}
            />
            <InputLabel htmlFor="verify-rider-birthdate">{'Know rider\u2019s birthday'}</InputLabel>
          </Box>
        </Box>
      </Box>
    </ContactGroupFormContainer>
  );
};
